import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { simTokens } from './tokens';

type SimShellProps = {
  children: React.ReactNode;
  header?: React.ReactNode;
  fixedAction?: React.ReactNode;
  maxWidthClassName?: string;
  className?: string;
  contentClassName?: string;
  headerClassName?: string;
  headerFullWidth?: boolean;
  headerInnerClassName?: string;
  mainTopClassName?: string;
  headerUnwrapped?: boolean;
  shellOffsetClassName?: string;
  dynamicHeaderOffset?: boolean;
};

type SimShellDebugState = {
  h1: number;
  h2: number;
  stack: number;
  bodyOffsetTarget: string;
  bodyWrapPaddingTop: string;
  bodyWrapTop: number;
  bodyFirstTop: number | null;
  scrollContainer: string;
  scrollContainerOverflow: string;
  bodyWrapPosition: string;
  bodyWrapOffsetParent: string;
  ancestorFlags: string[];
};

const SimShell: React.FC<SimShellProps> = ({
  children,
  header,
  fixedAction,
  maxWidthClassName = 'max-w-5xl',
  className = '',
  contentClassName = 'space-y-6',
  headerClassName,
  headerFullWidth = false,
  headerInnerClassName,
  mainTopClassName = 'mt-6',
  headerUnwrapped = false,
  shellOffsetClassName = '',
  dynamicHeaderOffset = false,
}) => {
  const shellRootRef = useRef<HTMLDivElement | null>(null);
  const bodyWrapRef = useRef<HTMLDivElement | null>(null);
  const mainRef = useRef<HTMLElement | null>(null);
  const [debugState, setDebugState] = useState<SimShellDebugState | null>(null);
  const resolvedHeaderClassName = headerClassName || simTokens.header;
  const resolvedHeaderInnerClassName = headerInnerClassName || maxWidthClassName;
  const uiDebugEnabled = useMemo(() => {
    if (!import.meta.env.DEV) return false;
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('ui_debug') === '1';
  }, []);

  const describeElement = (element: HTMLElement | null | undefined): string => {
    if (!element) return 'none';
    const dataIv = element.getAttribute('data-iv');
    if (dataIv) return `[data-iv="${dataIv}"]`;
    if (element.id) return `#${element.id}`;
    const className = (element.className || '').toString().trim();
    if (className) return `${element.tagName.toLowerCase()}.${className.split(/\s+/).slice(0, 2).join('.')}`;
    return element.tagName.toLowerCase();
  };

  const findScrollContainer = (
    startElement: HTMLElement | null
  ): { label: string; overflow: string } => {
    if (!startElement || typeof window === 'undefined') {
      return { label: 'window', overflow: 'documentElement' };
    }
    let current: HTMLElement | null = startElement;
    while (current) {
      const style = window.getComputedStyle(current);
      const overflowY = style.overflowY.toLowerCase();
      if (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        current.scrollHeight > current.clientHeight + 1
      ) {
        return {
          label: describeElement(current),
          overflow: `overflow-x:${style.overflowX.toLowerCase()} overflow-y:${overflowY}`,
        };
      }
      current = current.parentElement;
    }
    const documentElementStyle = window.getComputedStyle(document.documentElement);
    return {
      label: 'window',
      overflow: `html(overflow-x:${documentElementStyle.overflowX.toLowerCase()} overflow-y:${documentElementStyle.overflowY.toLowerCase()})`,
    };
  };

  const collectAncestorFlags = (startElement: HTMLElement | null): string[] => {
    if (!startElement || typeof window === 'undefined') return [];
    const flags: string[] = [];
    let current = startElement.parentElement;
    while (current) {
      const style = window.getComputedStyle(current);
      const overflowX = style.overflowX.toLowerCase();
      const overflowY = style.overflowY.toLowerCase();
      if (overflowX !== 'visible' || overflowY !== 'visible') {
        flags.push(`${describeElement(current)} overflow-x:${overflowX} overflow-y:${overflowY}`);
      }
      if (style.transform !== 'none') {
        flags.push(`${describeElement(current)} transform:${style.transform}`);
      }
      if (style.filter !== 'none') {
        flags.push(`${describeElement(current)} filter:${style.filter}`);
      }
      if (style.perspective !== 'none') {
        flags.push(`${describeElement(current)} perspective:${style.perspective}`);
      }
      current = current.parentElement;
    }
    return flags;
  };

  useLayoutEffect(() => {
    if (!dynamicHeaderOffset && !uiDebugEnabled) return;
    const shellRoot = shellRootRef.current;
    const bodyWrap = bodyWrapRef.current;
    const main = mainRef.current;
    if (!shellRoot) return;

    const getHeaderBars = () => {
      const line1 = shellRoot.querySelector<HTMLElement>('[data-iv-header-line="1"]');
      const line2 = shellRoot.querySelector<HTMLElement>('[data-iv-header-line="2"]');
      return { line1, line2 };
    };

    const measureAndSetHeaderVars = (source: 'mount' | 'resize' | 'observer') => {
      const { line1, line2 } = getHeaderBars();
      if (!line1 || !line2) return;
      line1.setAttribute('data-iv', 'header-line-1');
      line2.setAttribute('data-iv', 'header-line-2');
      const firstMeaningful = main?.firstElementChild;
      if (firstMeaningful && !firstMeaningful.getAttribute('data-iv')) {
        (firstMeaningful as HTMLElement).setAttribute('data-iv', 'body-first');
      }
      const h1 = Math.max(0, Math.round(line1.getBoundingClientRect().height));
      const h2 = Math.max(0, Math.round(line2.getBoundingClientRect().height));
      const stack = h1 + h2;
      shellRoot.style.setProperty('--iv-header-h1', `${h1}px`);
      shellRoot.style.setProperty('--iv-header-h2', `${h2}px`);
      shellRoot.style.setProperty('--iv-header-stack', `${stack}px`);

      if (!uiDebugEnabled || !bodyWrap) return;

      const bodyWrapStyles = window.getComputedStyle(bodyWrap);
      const bodyFirst = main?.querySelector<HTMLElement>('[data-iv="body-first"]') || null;
      const scrollContainerInfo = findScrollContainer(bodyWrap);
      const bodyOffsetTarget = shellRoot.classList.contains('iv-body-offset')
        ? describeElement(shellRoot)
        : bodyWrap.classList.contains('iv-body-offset')
          ? describeElement(bodyWrap)
          : 'none';
      const debugPayload: SimShellDebugState = {
        h1,
        h2,
        stack,
        bodyOffsetTarget,
        bodyWrapPaddingTop: bodyWrapStyles.paddingTop,
        bodyWrapTop: Math.round(bodyWrap.getBoundingClientRect().top),
        bodyFirstTop: bodyFirst ? Math.round(bodyFirst.getBoundingClientRect().top) : null,
        scrollContainer: scrollContainerInfo.label,
        scrollContainerOverflow: scrollContainerInfo.overflow,
        bodyWrapPosition: bodyWrapStyles.position,
        bodyWrapOffsetParent: describeElement(bodyWrap.offsetParent as HTMLElement | null),
        ancestorFlags: collectAncestorFlags(bodyWrap),
      };

      setDebugState(debugPayload);
      console.log(
        '[iv-ui-debug]',
        JSON.stringify(
          {
            source,
            ...debugPayload,
          },
          null,
          2
        )
      );
    };

    measureAndSetHeaderVars('mount');
    const { line1, line2 } = getHeaderBars();

    const onWindowResize = () => {
      measureAndSetHeaderVars('resize');
    };
    window.addEventListener('resize', onWindowResize);

    if (typeof ResizeObserver === 'undefined' || !line1 || !line2) {
      return () => {
        window.removeEventListener('resize', onWindowResize);
      };
    }

    const observer = new ResizeObserver(() => {
      measureAndSetHeaderVars('observer');
    });
    observer.observe(line1);
    observer.observe(line2);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onWindowResize);
    };
  }, [dynamicHeaderOffset, header, uiDebugEnabled]);

  return (
    <div
      ref={shellRootRef}
      className={`iv-sim ${simTokens.shell} ${shellOffsetClassName} ${className}`}
    >
      {header && headerFullWidth && headerUnwrapped ? header : null}
      {header && headerFullWidth && !headerUnwrapped ? (
        <header className={resolvedHeaderClassName}>
          {headerInnerClassName === '' ? (
            header
          ) : (
            <div className={`mx-auto w-full ${resolvedHeaderInnerClassName}`}>{header}</div>
          )}
        </header>
      ) : null}
      <div
        ref={bodyWrapRef}
        data-iv="body-wrap"
        className={`mx-auto w-full ${dynamicHeaderOffset ? 'iv-body-offset' : ''} ${maxWidthClassName}`}
      >
        {header && !headerFullWidth ? <header className={resolvedHeaderClassName}>{header}</header> : null}
        <main
          ref={mainRef}
          className={
            header
              ? `${mainTopClassName} ${contentClassName} ${fixedAction ? 'pb-28 sm:pb-32' : ''}`
              : `${contentClassName} ${fixedAction ? 'pb-28 sm:pb-32' : ''}`
          }
        >
          {children}
        </main>
      </div>
      {fixedAction ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white/95 backdrop-blur">
          <div className={`mx-auto flex w-full ${maxWidthClassName} justify-end px-5 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:px-6`}>
            <div className="pointer-events-auto w-full sm:w-auto">{fixedAction}</div>
          </div>
        </div>
      ) : null}
      {uiDebugEnabled ? (
        <>
          <div className="iv-debug-stack-line pointer-events-none fixed inset-x-0 z-[220] h-px bg-danger" />
          <aside className="fixed bottom-4 right-4 z-[230] max-h-[70vh] w-[360px] overflow-y-auto rounded-input border border-line bg-surface-card p-3 text-[11px] text-txt-primary shadow-raised">
            <p className="mb-2 font-semibold uppercase tracking-[0.12em] text-brand-700">UI Debug</p>
            <dl className="space-y-1">
              <div className="flex justify-between gap-3"><dt>h1</dt><dd>{debugState?.h1 ?? '-'}</dd></div>
              <div className="flex justify-between gap-3"><dt>h2</dt><dd>{debugState?.h2 ?? '-'}</dd></div>
              <div className="flex justify-between gap-3"><dt>stack</dt><dd>{debugState?.stack ?? '-'}</dd></div>
              <div className="flex justify-between gap-3"><dt>offset target</dt><dd className="truncate">{debugState?.bodyOffsetTarget ?? '-'}</dd></div>
              <div className="flex justify-between gap-3"><dt>bodyWrap padding-top</dt><dd>{debugState?.bodyWrapPaddingTop ?? '-'}</dd></div>
              <div className="flex justify-between gap-3"><dt>bodyWrap top</dt><dd>{debugState?.bodyWrapTop ?? '-'}</dd></div>
              <div className="flex justify-between gap-3"><dt>bodyFirst top</dt><dd>{debugState?.bodyFirstTop ?? '-'}</dd></div>
              <div className="flex justify-between gap-3"><dt>scroll container</dt><dd className="truncate">{debugState?.scrollContainer ?? '-'}</dd></div>
              <div className="flex justify-between gap-3"><dt>scroll overflow</dt><dd className="truncate">{debugState?.scrollContainerOverflow ?? '-'}</dd></div>
              <div className="flex justify-between gap-3"><dt>bodyWrap position</dt><dd>{debugState?.bodyWrapPosition ?? '-'}</dd></div>
              <div className="flex justify-between gap-3"><dt>offset parent</dt><dd className="truncate">{debugState?.bodyWrapOffsetParent ?? '-'}</dd></div>
            </dl>
            <div className="mt-2 border-t border-line-subtle pt-2">
              <p className="mb-1 font-semibold uppercase tracking-[0.12em] text-txt-secondary">ancestor flags</p>
              {debugState?.ancestorFlags?.length ? (
                <ul className="space-y-1 text-txt-secondary">
                  {debugState.ancestorFlags.map((flag) => (
                    <li key={flag} className="break-words">{flag}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-txt-muted">none</p>
              )}
            </div>
          </aside>
        </>
      ) : null}
    </div>
  );
};

export default SimShell;
